import * as React from "react";
import { TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { DynamicNotificationTimeMetaFieldProps } from "../interface";

const useStyles = makeStyles({
    wrapper: {
        margin: "10px 10px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
    },
    row: {
        display: "flex",
        flexWrap: "wrap",
        gap: "12px",
    },
    field: {
        minWidth: "180px",
    },
});

const DEFAULT_DYNAMIC_NOTIFICATION_TIME = {
    invoice: "04:30",
    assignment: "04:00",
    ticket: "04:00",
    service_agreement: "04:00",
};

const normalizeTimeValue = (value: string) => {
    const match = value.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) {
        return value;
    }
    const hours = String(Number(match[1])).padStart(2, "0");
    return `${hours}:${match[2]}`;
};

const isValid24HourTime = (value: string) =>
    /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);

const DynamicNotificationTimeMetaField = ({
    optionsSelectedHandler,
    featuresMetaData,
}: DynamicNotificationTimeMetaFieldProps) => {
    const classes = useStyles();
    const [errors, setErrors] = React.useState<Record<string, string>>({});
    const dynamicTime = {
        ...DEFAULT_DYNAMIC_NOTIFICATION_TIME,
        ...(featuresMetaData.DYNAMIC_NOTIFICATION_TIME || {}),
    };

    const handleChange = (key: keyof typeof DEFAULT_DYNAMIC_NOTIFICATION_TIME) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const updated = {
                ...dynamicTime,
                [key]: event.target.value,
            };
            optionsSelectedHandler(updated);
        };

    const handleBlur = (key: keyof typeof DEFAULT_DYNAMIC_NOTIFICATION_TIME) =>
        (event: React.FocusEvent<HTMLInputElement>) => {
            const normalized = normalizeTimeValue(event.target.value);
            if (!isValid24HourTime(normalized)) {
                setErrors((prev) => ({
                    ...prev,
                    [key]: "Enter time in 24-hour format (HH:mm).",
                }));
                return;
            }
            setErrors((prev) => {
                const updated = { ...prev };
                delete updated[key];
                return updated;
            });
            if (normalized === event.target.value) {
                return;
            }
            const updated = {
                ...dynamicTime,
                [key]: normalized,
            };
            optionsSelectedHandler(updated);
        };

    return (
        <div className={classes.wrapper}>
            <Typography variant="body2">
                Enter UTC time in 24-hour format (HH:mm). Use a leading zero
                for single-digit hours (e.g., 07:00).
            </Typography>
            <div className={classes.row}>
                <TextField
                    className={classes.field}
                    label="Invoice (UTC)"
                    type="text"
                    value={dynamicTime.invoice}
                    onChange={handleChange("invoice")}
                    onBlur={handleBlur("invoice")}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ placeholder: "HH:mm", inputMode: "numeric" }}
                    error={Boolean(errors.invoice)}
                    helperText={errors.invoice || " "}
                />
                <TextField
                    className={classes.field}
                    label="Assignment (UTC)"
                    type="text"
                    value={dynamicTime.assignment}
                    onChange={handleChange("assignment")}
                    onBlur={handleBlur("assignment")}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ placeholder: "HH:mm", inputMode: "numeric" }}
                    error={Boolean(errors.assignment)}
                    helperText={errors.assignment || " "}
                />
                <TextField
                    className={classes.field}
                    label="Ticket (UTC)"
                    type="text"
                    value={dynamicTime.ticket}
                    onChange={handleChange("ticket")}
                    onBlur={handleBlur("ticket")}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ placeholder: "HH:mm", inputMode: "numeric" }}
                    error={Boolean(errors.ticket)}
                    helperText={errors.ticket || " "}
                />
                <TextField
                    className={classes.field}
                    label="Service Agreement (UTC)"
                    type="text"
                    value={dynamicTime.service_agreement}
                    onChange={handleChange("service_agreement")}
                    onBlur={handleBlur("service_agreement")}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ placeholder: "HH:mm", inputMode: "numeric" }}
                    error={Boolean(errors.service_agreement)}
                    helperText={errors.service_agreement || " "}
                />
            </div>
        </div>
    );
};

export default DynamicNotificationTimeMetaField;
